import React, { useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CImage,
  CListGroup,
  CListGroupItem,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AppLoader from "../../components/seller/AppLoader";
import { fetchReceivedOffersDetail } from "../../features/seller/sellerSlice";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";
import { formatCurrency } from "../../utils/price";

export default function SellerOfferDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    data: offer,
    loading,
    error,
  } = useSelector((state) => state.seller.receivedOffersDetail);

  useEffect(() => {
    if (id) dispatch(fetchReceivedOffersDetail(id));
  }, [id, dispatch]);

  if (loading) return <AppLoader />;
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!offer || !offer.product) return null;

  /* =============================
     OFFER LEVEL DESTRUCTURE
     ============================= */

  /* =============================
     PRODUCT LEVEL DESTRUCTURE
     ============================= */
  const {
    unique_offer_id,
    created_at,
    status,
    buyer,
    name,
    description,
    image_url,
    quantity,
    unit_of_measurement,
    location,
    delivery_port,
    metal_code,
    sorting_type,
    attachment,
    waste_range,
    toxic_level,
    hazard_types,
    hazard_description,
    max_buying_price,
    discount_percentage,
    discounted_price,
    lead_time,
    incoterms,
    payment_terms,
    specifications,
    faqs,
    category,
    subcategory,
  } = offer.product;

  const show = (v, fallback = "N/A") =>
    v === null || v === undefined || v === "" ? fallback : v;

  const isHazardous = hazard_types?.includes("hazardous");

  const STATUS_COLORS = {
    approved: "success",
    pending: "warning",
    rejected: "danger",
    default: "secondary",
  };

  const statusColor =
    STATUS_COLORS[String(status).toLowerCase()] || STATUS_COLORS.default;

  const formatAUD = (value) => {
    if (value === null || value === undefined || value === "") return "A$ 0";
    return `A$ ${Number(value).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    })}`;
  };

  return (
    <>
      {/* ================= HEADER ================= */}
      <CRow className="mb-4">
        <CCol xs={12}>
          <CCard>
            <CCardBody className="d-flex gap-4 align-items-start">
              <CImage src={image_url} width={160} rounded />

              <div className="flex-grow-1">
                <h4 className="mb-2">{show(name)}</h4>

                <div className="text-muted mb-2">
                  Offer ID: {show(unique_offer_id)}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Status:</span>
                  <CBadge color={statusColor}>
                    {capitalizeFirst(show(status))}
                  </CBadge>
                </div>

                <div className="text-muted mt-1">
                  Created {timeAgo(created_at)}
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= ROW 1: BASIC + TECH ================= */}
      <CRow className="g-4 mb-2">
        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Basic Details</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Category:</strong> {show(category?.name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Subcategory:</strong> {show(subcategory?.name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Metal Code:</strong> {show(metal_code)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Location:</strong> {show(location)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Description:</strong>
                <div
                  className="mt-2 ql-editor"
                  style={{ padding: 0 }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>

        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Technical Specifications</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Quantity:</strong> {show(quantity)}{" "}
                {show(unit_of_measurement)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Sorting Type:</strong> {show(capitalizeFirst(sorting_type))}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Attachment:</strong> {show(attachment)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Waste Range %:</strong> {show(waste_range)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Hazard:</strong>{" "}
                {isHazardous ? "Hazardous" : "Non-Hazardous"}
              </CListGroupItem>
              {isHazardous && (
                <CListGroupItem>
                  <strong>Description:</strong>
                  <div
                    className="mt-2 ql-editor"
                    style={{ padding: 0 }}
                    dangerouslySetInnerHTML={{ __html: hazard_description }}
                  />
                </CListGroupItem>
              )}
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= PRICING ================= */}
      <CRow className="g-4 mb-2">
        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Pricing</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Max Buying Price:</strong> {formatCurrency(max_buying_price)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Discount %:</strong> {show(discount_percentage)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Discounted Price:</strong> {formatCurrency(discounted_price)}
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>

        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Shipping & Trade Terms</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Incoterms:</strong> {show(incoterms)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Lead Time:</strong> {show(lead_time)} days
              </CListGroupItem>
              <CListGroupItem>
                <strong>Payment Terms:</strong> {show(payment_terms)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Delivery Port:</strong> {show(delivery_port)}
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= BUYER ================= */}
      <CRow className="mb-2">
        <CCol xs={12}>
          <CCard>
            <CCardHeader>Buyer Information</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Name:</strong> {show(buyer?.name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Company:</strong> {show(buyer?.company_name)}
              </CListGroupItem>
              {/* <CListGroupItem>
                <strong>Email:</strong> {show(buyer?.email)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Phone:</strong> {show(buyer?.phone)}
              </CListGroupItem> */}
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= SPECIFICATIONS ================= */}
      {/* {specifications?.length > 0 && (
        <CRow className="mb-2">
          <CCol xs={12}>
            <CCard>
              <CCardHeader>Specifications</CCardHeader>
              <CListGroup flush>
                {specifications.map((spec) => (
                  <CListGroupItem key={spec.id}>
                    <strong>{spec.name}:</strong> {spec.value}
                  </CListGroupItem>
                ))}
              </CListGroup>
            </CCard>
          </CCol>
        </CRow>
      )} */}

      {/* ================= FAQS ================= */}
      {faqs?.length > 0 && (
        <CRow>
          <CCol xs={12}>
            <CCard>
              <CCardHeader>FAQs</CCardHeader>
              <CCardBody>
                <CAccordion>
                  {faqs.map((faq, i) => (
                    <CAccordionItem key={faq.id} itemKey={i}>
                      <CAccordionHeader>{faq.question}</CAccordionHeader>
                      <CAccordionBody>{faq.answer}</CAccordionBody>
                    </CAccordionItem>
                  ))}
                </CAccordion>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}
    </>
  );
}
